import { useState } from 'react'
import { useMaterialPricing, MaterialItem } from '@/hooks/useMaterialPricing'

const CATEGORY_LABELS: Record<string, string> = {
  all: 'All Materials',
  concrete: 'Ready-Mix Concrete',
  bags: 'Bagged Concrete',
  reinforcement: 'Reinforcement',
  blocks: 'Blocks',
  forms: 'Forms & Tubes',
  labor: 'Labor Rates',
  delivery: 'Delivery',
}

interface MaterialPricingPanelProps {
  defaultCategory?: string
  compact?: boolean
  onPriceSelect?: (material: MaterialItem) => void
}

function TrendBadge({ trend }: { trend?: MaterialItem['trend'] }) {
  if (!trend) {
    return <span className="text-[10px] text-gray-400">No trend yet</span>
  }

  if (trend.direction === 'flat') {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-gray-500">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
        </svg>
        Flat (7d)
      </span>
    )
  }

  const isUp = trend.direction === 'up'
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${
        isUp ? 'text-red-600' : 'text-green-600'
      }`}
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        {isUp ? (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        ) : (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0v-8m0 8l-8-8-4 4-6-6" />
        )}
      </svg>
      {isUp ? '+' : ''}
      {trend.changePercent.toFixed(1)}% (7d)
    </span>
  )
}

function Sparkline({ points }: { points: { date: string; priceAvg: number }[] }) {
  if (points.length < 2) return null

  const values = points.map((p) => p.priceAvg)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = max - min || 1
  const w = 260
  const h = 56
  const step = w / (points.length - 1)

  const coords = values.map((v, i) => {
    const x = i * step
    const y = h - ((v - min) / range) * (h - 8) - 4
    return `${x.toFixed(1)},${y.toFixed(1)}`
  })

  const lastUp = values[values.length - 1] >= values[0]

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-14" preserveAspectRatio="none">
      <polyline
        points={coords.join(' ')}
        fill="none"
        stroke={lastUp ? '#dc2626' : '#16a34a'}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {coords.map((c, i) => {
        const [x, y] = c.split(',')
        return <circle key={i} cx={x} cy={y} r={2.5} fill={lastUp ? '#dc2626' : '#16a34a'} />
      })}
    </svg>
  )
}

export function MaterialPricingPanel({
  defaultCategory = 'all',
  compact = false,
  onPriceSelect,
}: MaterialPricingPanelProps) {
  const {
    pricing,
    loading,
    error,
    region,
    category,
    changeRegion,
    changeCategory,
    history,
    historyLoading,
    historyError,
    fetchHistory,
    clearHistory,
  } = useMaterialPricing('national', defaultCategory)

  const [expandedMaterial, setExpandedMaterial] = useState<string | null>(null)

  const toggleHistory = (materialName: string) => {
    if (expandedMaterial === materialName) {
      setExpandedMaterial(null)
      clearHistory()
      return
    }
    setExpandedMaterial(materialName)
    fetchHistory(materialName, region, 30)
  }

  if (loading) {
    return (
      <div className="bg-white border-2 border-gray-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-5 h-5 border-2 border-[#FF8C00] border-t-transparent rounded-full animate-spin" />
          <span className="text-[#4A4A4A] font-semibold">Loading real-time material prices...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
        <p className="text-red-700 font-semibold text-sm">Unable to load pricing: {error}</p>
      </div>
    )
  }

  if (!pricing) return null

  const groupedMaterials = pricing.materials.reduce<Record<string, MaterialItem[]>>((acc, m) => {
    if (!acc[m.category]) acc[m.category] = []
    acc[m.category].push(m)
    return acc
  }, {})

  return (
    <div className="bg-white border-2 border-gray-200 rounded-lg shadow-sm overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#4A4A4A] to-[#5A5A5A] p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#FF8C00]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-white font-bold text-lg">Real-Time Material Pricing</h3>
          </div>
          <div className="flex items-center gap-1">
            <span className="inline-block w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            <span className="text-green-300 text-xs font-medium">Live</span>
            {pricing.seasonalAdjustment && (
              <span className="text-gray-300 text-xs ml-2">
                Season: {pricing.seasonalAdjustment}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className={`p-4 bg-gray-50 border-b border-gray-200 ${compact ? 'flex flex-col gap-3' : 'flex flex-col sm:flex-row gap-4'}`}>
        <div className="flex-1">
          <label htmlFor="pricing-region" className="block text-xs font-semibold text-[#4A4A4A] mb-1">
            Region
          </label>
          <select
            id="pricing-region"
            value={region}
            onChange={(e) => {
              changeRegion(e.target.value)
              setExpandedMaterial(null)
              clearHistory()
            }}
            className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none bg-white"
          >
            {pricing.availableRegions?.map((r) => (
              <option key={r.code} value={r.code}>
                {r.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex-1">
          <label htmlFor="pricing-category" className="block text-xs font-semibold text-[#4A4A4A] mb-1">
            Category
          </label>
          <select
            id="pricing-category"
            value={category}
            onChange={(e) => {
              changeCategory(e.target.value)
              setExpandedMaterial(null)
              clearHistory()
            }}
            className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none bg-white"
          >
            <option value="all">All Materials</option>
            <option value="concrete">Ready-Mix Concrete</option>
            <option value="bags">Bagged Concrete</option>
            <option value="reinforcement">Reinforcement</option>
            <option value="blocks">Blocks</option>
            <option value="forms">Forms & Tubes</option>
            <option value="labor">Labor Rates</option>
            <option value="delivery">Delivery</option>
          </select>
        </div>
      </div>

      {/* Materials Table */}
      <div className="p-4">
        {Object.entries(groupedMaterials).map(([cat, materials]) => (
          <div key={cat} className="mb-4 last:mb-0">
            {category === 'all' && (
              <h4 className="text-sm font-bold text-[#4A4A4A] uppercase tracking-wide mb-2 flex items-center gap-2">
                <span className="w-2 h-2 bg-[#FF8C00] rounded-full" />
                {CATEGORY_LABELS[cat] || cat}
              </h4>
            )}
            <div className="space-y-1.5">
              {materials.map((m, i) => (
                <div key={i} className="rounded-md border border-gray-100 overflow-hidden">
                  <div
                    className={`flex items-center justify-between p-3 hover:border-[#FF8C00] hover:bg-orange-50 transition-all ${
                      onPriceSelect ? 'cursor-pointer' : ''
                    }`}
                    onClick={() => onPriceSelect?.(m)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#4A4A4A] truncate">{m.name}</p>
                      <p className="text-xs text-gray-500">{m.unit}</p>
                      <div className="mt-1">
                        <TrendBadge trend={m.trend} />
                      </div>
                    </div>
                    <div className="text-right ml-4 flex-shrink-0 flex items-center gap-3">
                      <div>
                        <p className="text-base font-bold text-[#FF8C00]">
                          ${m.priceAvg.toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500">
                          ${m.priceMin.toFixed(2)} - ${m.priceMax.toFixed(2)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleHistory(m.name)
                        }}
                        title="View price history"
                        aria-expanded={expandedMaterial === m.name}
                        className={`p-1.5 rounded-md border ${
                          expandedMaterial === m.name
                            ? 'bg-[#4A4A4A] border-[#4A4A4A] text-white'
                            : 'border-gray-300 text-[#4A4A4A] hover:border-[#FF8C00] hover:text-[#FF8C00]'
                        } transition-colors`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3v18h18M7 14l4-4 3 3 5-6" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {expandedMaterial === m.name && (
                    <div className="border-t border-gray-100 bg-gray-50 p-3">
                      {historyLoading && (
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <div className="w-3.5 h-3.5 border-2 border-[#FF8C00] border-t-transparent rounded-full animate-spin" />
                          Loading 30-day price history...
                        </div>
                      )}
                      {historyError && (
                        <p className="text-xs text-red-600 font-medium">{historyError}</p>
                      )}
                      {!historyLoading && !historyError && history && history.material === m.name && (
                        <>
                          {history.dataPoints < 2 ? (
                            <p className="text-xs text-gray-500">
                              {history.note || 'Not enough history yet. Daily price snapshots are recorded automatically, check back tomorrow.'}
                            </p>
                          ) : (
                            <>
                              <Sparkline points={history.trend} />
                              <div className="flex items-center justify-between mt-2 text-xs">
                                <span className="text-gray-500">
                                  {history.summary.startDate} → {history.summary.endDate}
                                </span>
                                <span
                                  className={`font-bold ${
                                    history.summary.direction === 'up'
                                      ? 'text-red-600'
                                      : history.summary.direction === 'down'
                                      ? 'text-green-600'
                                      : 'text-gray-500'
                                  }`}
                                >
                                  ${history.summary.startPrice?.toFixed(2)} → ${history.summary.endPrice?.toFixed(2)}{' '}
                                  ({history.summary.direction === 'up' ? '+' : ''}
                                  {history.summary.changePercent?.toFixed(1)}%)
                                </span>
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
        <p className="text-xs text-gray-500 leading-relaxed">
          {pricing.disclaimer}
        </p>
        <p className="text-xs text-gray-400 mt-1">
          Updated: {new Date(pricing.generatedAt).toLocaleString()}
          {pricing.trendWindowDays && ` · Trend window: ${pricing.trendWindowDays} days`}
        </p>
      </div>
    </div>
  )
}
